import React, {useCallback, useEffect, useRef, useMemo} from 'react';
import {Map} from 'immutable';
import {QuestionModelV3} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';
import {callTargetedAction} from 'client/framework';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';

import PassageCorrectionPreSubmissionRenderer from '../PassageCorrectionPreSubmissionRenderer.react';
import PassageCorrectionPostSubmissionRenderer from '../PassageCorrectionPostSubmissionRenderer.react';
import passageCorrectionQuestionActions from '../PassageCorrectionQuestion.actions';
import {
  useQuestionTranslationStore,
  NoTranslationWarningHeader,
  btnDisabledWarning
} from '../../shared/TranslationSection';

import LegacyPassageCorrectionInputArea from '../legacy/PassageCorrectionInputArea.react';

interface PassageCorrectionInputAreaProps {
  storeName: string;
  question: QuestionModelV3;
  isGuessSubmitted: boolean;
}

const PassageCorrectionInputArea = ({
  isGuessSubmitted,
  ...props
}: PassageCorrectionInputAreaProps) => {
  const initialCorrections = useRef(Map());

  const {showAnswerTranslation, translationInfo} = useQuestionTranslationStore(
    useShallow((state) => ({
      showAnswerTranslation: state.toggleStatus.answer,
      translationInfo: state.translatedQuestionInfo
    }))
  );

  useEffect(() => {
    if (!props.question.getStore().hasInitialCorrections()) {
      callTargetedAction({
        name: passageCorrectionQuestionActions.SET_INITIAL_CORRECTIONS,
        payload: initialCorrections.current,
        targetStore: props.question.getStore().getName()
      });
    }
  }, [props.question]);

  const addWordToInitialCorrection = useCallback(
    ({text, matchedRegionIndex, optionIndex}) => {
      if (matchedRegionIndex === undefined || props.question.getStore().hasInitialCorrections()) {
        return;
      }
      initialCorrections.current = initialCorrections.current.setIn(
        [matchedRegionIndex, optionIndex],
        text
      );
    },
    [props.question]
  );

  const translatedText = useMemo(() => {
    return [translationInfo?.uncorrected_text];
  }, [translationInfo]);

  return (
    <div className='passage-correction-question'>
      {showAnswerTranslation && <NoTranslationWarningHeader text={btnDisabledWarning} />}
      <h2 className='question__prompt-heading'>
        {!isGuessSubmitted
          ? 'Click the word(s) you want to edit in the sentence below and type your changes.'
          : 'Click highlighted areas for more information.'}
      </h2>
      {!isGuessSubmitted ? (
        <PassageCorrectionPreSubmissionRenderer
          {...props}
          onRenderWordFunc={addWordToInitialCorrection}
          isGuessSubmitted={isGuessSubmitted}
          showTranslation={showAnswerTranslation}
          translatedText={translatedText}
        />
      ) : (
        <PassageCorrectionPostSubmissionRenderer
          {...props}
          onRenderWordFunc={addWordToInitialCorrection}
          isGuessSubmitted={isGuessSubmitted}
          showTranslation={showAnswerTranslation}
          translatedText={translatedText}
        />
      )}
    </div>
  );
};

const PassageCorrectionInputAreaWrapper = (props: PassageCorrectionInputAreaProps) => {
  return (
    <FeatureFlag name='assignment_question_translations'>
      <PassageCorrectionInputArea {...props} />
      <FeatureFlag.Fallback>
        <LegacyPassageCorrectionInputArea {...props} />
      </FeatureFlag.Fallback>
    </FeatureFlag>
  );
};

export default PassageCorrectionInputAreaWrapper;
